// =============================================================================
// DriverRemittancesPage — list
// =============================================================================
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Money } from '../../../components/Money';
import { StatusPill } from '../../../components/StatusPill';
import { EmptyState } from '../../../components/EmptyState';
import { PageScaffold } from '../../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../../components/DataTable';
import { cn } from '../../../lib/cn';

type Status = 'PENDING' | 'ACKNOWLEDGED' | 'DISPUTED';
type Source = 'DELIVERY_COD' | 'SHOP_DRAWER';

interface RemRow {
  id: string;
  number: string;
  status: Status;
  source: Source;
  claimedAmount: string;
  actualAmount: string | null;
  submittedAt: string;
  acknowledgedAt: string | null;
  driver: { id: string; name: string };
  acknowledgedBy: { id: string; name: string } | null;
  shop: { id: string; code: string; name: string } | null;
  lines: Array<{ id: string; amount: string }>;
}

type Tab = 'all' | 'pending' | 'acknowledged' | 'disputed';
const TABS: { key: Tab; label: string; match: (s: Status) => boolean }[] = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'pending', label: 'Pending count', match: (s) => s === 'PENDING' },
  { key: 'acknowledged', label: 'Acknowledged', match: (s) => s === 'ACKNOWLEDGED' },
  { key: 'disputed', label: 'Disputed', match: (s) => s === 'DISPUTED' },
];

function tone(s: Status): 'warning' | 'positive' | 'negative' {
  if (s === 'ACKNOWLEDGED') return 'positive';
  if (s === 'DISPUTED') return 'negative';
  return 'warning';
}

export function DriverRemittancesPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('all');
  const list = useQuery({
    queryKey: ['driver-remittances'],
    queryFn: () => apiGet<RemRow[]>('/driver-remittances'),
    refetchInterval: 60_000,
  });

  const filtered = useMemo(() => {
    const def = TABS.find((t) => t.key === tab)!;
    return (list.data ?? []).filter((r) => def.match(r.status));
  }, [list.data, tab]);

  const counts = useMemo(() => {
    const out: Record<Tab, number> = { all: 0, pending: 0, acknowledged: 0, disputed: 0 };
    for (const r of list.data ?? []) {
      out.all += 1;
      const k =
        r.status === 'PENDING'
          ? 'pending'
          : r.status === 'ACKNOWLEDGED'
            ? 'acknowledged'
            : 'disputed';
      out[k] += 1;
    }
    return out;
  }, [list.data]);

  const pendingTotal = useMemo(
    () =>
      (list.data ?? [])
        .filter((r) => r.status === 'PENDING')
        .reduce((sum, r) => sum + Number(r.claimedAmount), 0),
    [list.data],
  );

  const HEADER_ACTIONS = (
    <Link to="/cash-handover">
      <Button variant="secondary">+ Shop handover</Button>
    </Link>
  );

  return (
    <PageScaffold
      title="Driver Remittances"
      subtitle="Delivery-COD bags + shop drawer handovers in one feed. Acknowledge = post Dr HQ Cash, Cr Cash in Transit."
      actions={HEADER_ACTIONS}
      scroll={false}
    >
      {pendingTotal > 0 && (
        <Card bodyClassName="px-6 py-4 bg-warning-soft border-warning/30" className="shrink-0">
          <div className="text-xs uppercase tracking-wider text-warning">
            In transit · awaiting count
          </div>
          <div className="text-2xl kc-serif text-ink mt-1">
            <Money value={pendingTotal} />
          </div>
          <div className="text-xs text-ink-3 mt-0.5">
            {counts.pending} remittance{counts.pending === 1 ? '' : 's'} pending
          </div>
        </Card>
      )}

      <Card bodyClassName="p-4" className="shrink-0">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs uppercase tracking-wide transition border',
                tab === t.key
                  ? 'bg-accent text-white border-accent'
                  : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40',
              )}
            >
              <span>{t.label}</span>
              <span
                className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded',
                  tab === t.key ? 'bg-white/20 text-white' : 'bg-surface text-ink-3',
                )}
              >
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>
      </Card>

      <DataTableCard>
        {list.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No remittances here"
            description="Drivers submit these after a delivery round."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">REM</th>
                <th className="px-6 py-2 font-medium">Source</th>
                <th className="px-6 py-2 font-medium">Driver</th>
                <th className="px-6 py-2 font-medium">When</th>
                <th className="px-6 py-2 font-medium text-right">Claimed</th>
                <th className="px-6 py-2 font-medium text-right">Counted</th>
                <th className="px-6 py-2 font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-rule-2 hover:bg-paper-2">
                  <td className="px-6 py-3">
                    <Link
                      to={`/remittances/${r.id}`}
                      className="kc-mono text-xs text-ink hover:text-accent"
                    >
                      {r.number}
                    </Link>
                  </td>
                  <td className="px-6 py-3">
                    {r.source === 'SHOP_DRAWER' ? (
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-2">
                        <span className="px-1.5 py-0.5 rounded bg-paper-2 border border-rule">
                          Shop
                        </span>
                        {r.shop?.code ?? '—'}
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-ink-2 px-1.5 py-0.5 rounded bg-paper-2 border border-rule">
                        Delivery · {r.lines.length}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-ink">{r.driver.name}</td>
                  <td className="px-6 py-3 text-xs text-ink-3">
                    {new Date(r.submittedAt).toLocaleString('en-MU', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Money value={r.claimedAmount} />
                  </td>
                  <td className="px-6 py-3 text-right">
                    {r.actualAmount ? (
                      <Money value={r.actualAmount} />
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill tone={tone(r.status)}>{r.status.toLowerCase()}</StatusPill>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Link to={`/remittances/${r.id}`}>
                      <Button size="sm" variant="ghost">
                        Open
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}
